import { handleErrorAndAlert } from '@zyno-io/vue-foundation';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { hasRouteAccess } from '@/shared/helpers/permissions.helpers';

// The standalone ZynoTalk mobile app. It has no universal links, so we deep-link via its custom URL
// scheme and fall back to the store listing when it isn't installed. Values are the production
// build's; see zynotalk-mobile app.json / .gitlab-ci.yml for the dev/alpha variants.
const ZynoTalkApp = {
    scheme: 'app.zyno.talk://',
    storeUrl: {
        ios: 'https://apps.apple.com/us/app/zynotalk/id6478135154',
        android: 'https://play.google.com/store/apps/details?id=app.zyno.talk'
    }
} as const;

/**
 * Which app store a phone-browser user should be sent to for ZynoTalk, or `null` when there is no
 * store hand-off to make: inside the native app (it has a bridge that queries install state) and on
 * desktop (no phone store, and the desktop Talk web screens work fine).
 */
export function talkBrowserStoreOS(): 'ios' | 'android' | null {
    if (MobileBridge.isWithinZynoSuiteMobile) return null;
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return null;
}

/**
 * Whether to surface the ZynoTalk launcher tile at all — Talk has no usable mobile web surface, so
 * for Talk-entitled tenants we offer a hand-off to the standalone app (native app) or its store
 * (phone browser). Desktop keeps the normal routing tile, so this is false there.
 */
export function showZynoTalkLauncher(): boolean {
    return hasRouteAccess('/talk') && (MobileBridge.isWithinZynoSuiteMobile || talkBrowserStoreOS() !== null);
}

/**
 * Launch ZynoTalk from the launcher tile. In the native app this hands off through the bridge (deep
 * link, store fallback when not installed). In a phone browser it uses the deep-link-then-timeout
 * trick below.
 */
export async function launchZynoTalk(): Promise<void> {
    if (MobileBridge.isWithinZynoSuiteMobile) {
        const platform = MobileBridge.config.value?.platform ?? 'ios';
        try {
            await MobileBridge.openExternalApp(ZynoTalkApp.scheme, ZynoTalkApp.storeUrl[platform]);
        } catch (err) {
            handleErrorAndAlert(err);
        }
        return;
    }

    const os = talkBrowserStoreOS();
    if (os) openAppOrStore(os);
}

/**
 * Best-effort "open the app, else its store" for a phone browser. We navigate to ZynoTalk's custom
 * URL scheme; if the app is installed it opens and the tab is backgrounded. We watch for that
 * background (visibilitychange → hidden, or pagehide) and, if it hasn't happened after a short grace
 * period, assume the app isn't installed and redirect to the store. We deliberately do NOT treat
 * window `blur` as "app opened" — iOS fires blur for its own scheme-permission / "Cannot Open Page"
 * dialogs, which would wrongly suppress the store fallback.
 *
 * Custom schemes can't be probed reliably on every browser (iOS may surface a "Cannot Open Page"
 * prompt when the app is absent), so this is a graceful fallback, not a guarantee.
 */
function openAppOrStore(os: 'ios' | 'android'): void {
    let backgrounded = false;
    const onVisibility = () => {
        if (document.visibilityState === 'hidden') backgrounded = true;
    };
    const onPageHide = () => {
        backgrounded = true;
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    // Attempt the deep link. If ZynoTalk is installed it opens and the tab backgrounds.
    window.location.href = ZynoTalkApp.scheme;

    window.setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onPageHide);
        // Still here and foregrounded → the app didn't open; send them to the store instead.
        if (!backgrounded && document.visibilityState === 'visible') {
            window.location.href = ZynoTalkApp.storeUrl[os];
        }
    }, 1000);
}
